/**
 * Week view: 7 day columns for the selected agendas (Fresha-style planning).
 * With >1 agenda each block carries the agenda's color dot (same as VIENDO chips).
 */
import React, { useMemo } from 'react';
import { RefreshControl, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { agenda, agendaFonts } from '../domain/agendaTheme';
import type { Appointment, AppointmentStatus, ResourceTimeOff } from '../domain/types';
import { timeOffsForLocalDay } from '../domain/dayBoardLayout';
import { PaperSheet } from '../../shared/ticket/PaperSheet';

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

function addDays(d: Date, n: number): Date {
  const next = new Date(d);
  next.setDate(next.getDate() + n);
  return next;
}

function isSameLocalDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
}

const STATUS_STYLE: Record<AppointmentStatus, { bg: string; border: string; text: string }> = {
  requested: agenda.requested,
  confirmed: agenda.confirmed,
  completed: agenda.completed,
  cancelled: agenda.cancelled,
  no_show: agenda.noShow,
};

const STATUS_LABELS: Record<AppointmentStatus, string> = {
  requested: 'Solicitado',
  confirmed: 'Confirmado',
  completed: 'Completado',
  cancelled: 'Cancelado',
  no_show: 'No vino',
};

/** Monday-start week containing `anchor`. */
export function weekDaysFrom(anchor: Date): Date[] {
  const d = startOfLocalDay(anchor);
  const dow = d.getDay(); // 0 Sun
  const mondayOffset = dow === 0 ? -6 : 1 - dow;
  const monday = addDays(d, mondayOffset);
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

type Props = {
  anchorDay: Date;
  /** Desktop: equal flex columns without horizontal scroll when they fit. */
  isWide?: boolean;
  /** Agendas shown (empty/undefined = all). Blocks get a color dot when >1. */
  resourceIds?: ReadonlyArray<string>;
  resourceColorById?: ReadonlyMap<string, string>;
  resourceNameById?: ReadonlyMap<string, string>;
  appointments: Appointment[];
  /** Time-offs (scoped by resourceIds like appointments); multi-day offs show each day. */
  timeOffs?: ResourceTimeOff[];
  productNameById: Map<string, string>;
  onSelectDay: (day: Date) => void;
  /** If set, the empty state becomes "Libre — ＋ turno" → create on that day. */
  onCreateAtDay?: (day: Date) => void;
  onPressAppointment: (a: Appointment) => void;
  refreshing?: boolean;
  onRefresh?: () => void;
};

export function AppointmentsWeekBoard(props: Props) {
  const days = useMemo(() => weekDaysFrom(props.anchorDay), [props.anchorDay]);
  const today = startOfLocalDay(new Date());
  const isWide = props.isWide === true;
  const ids = props.resourceIds;
  const multiAgenda = Boolean(ids && ids.length > 1);

  const byDay = useMemo(() => {
    const map = new Map<string, Appointment[]>();
    for (const day of days) map.set(day.toDateString(), []);
    for (const a of props.appointments) {
      if (a.status === 'cancelled') continue;
      if (ids && ids.length > 0 && !ids.includes(a.resourceId)) continue;
      const key = startOfLocalDay(new Date(a.startsAt)).toDateString();
      const list = map.get(key);
      if (list) list.push(a);
    }
    for (const list of map.values()) {
      list.sort((x, y) => x.startsAt.localeCompare(y.startsAt));
    }
    return map;
  }, [days, props.appointments, ids]);

  const offsByDay = useMemo(() => {
    const map = new Map<string, ResourceTimeOff[]>();
    const scoped = (props.timeOffs ?? []).filter(
      (t) => !ids || ids.length === 0 || ids.includes(t.resourceId),
    );
    for (const day of days) map.set(day.toDateString(), timeOffsForLocalDay(day, scoped));
    return map;
  }, [days, props.timeOffs, ids]);

  const columns = days.map((day) => {
    const list = byDay.get(day.toDateString()) ?? [];
    const dayOffs = offsByDay.get(day.toDateString()) ?? [];
    const isToday = isSameLocalDay(day, today);
    const isAnchor = isSameLocalDay(day, props.anchorDay);
    const dayLong = day.toLocaleDateString('es-AR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
    return (
      <View
        key={day.toISOString()}
        style={[styles.col, isWide && styles.colWide, isAnchor && styles.colAnchor]}
      >
        <TouchableOpacity
          style={[styles.colHead, isAnchor && styles.colHeadAnchor]}
          onPress={() => props.onSelectDay(day)}
          activeOpacity={0.85}
          accessibilityRole="button"
          accessibilityLabel={`${dayLong}${isToday ? ', hoy' : ''}, ${list.length} ${list.length === 1 ? 'turno' : 'turnos'} — ver día`}
        >
          <Text style={[styles.colDow, isToday && styles.colToday]}>
            {day.toLocaleDateString('es-AR', { weekday: 'short' })}
          </Text>
          <Text style={[styles.colNum, isToday && styles.colToday]}>{day.getDate()}</Text>
          {list.length > 0 ? (
            <Text style={styles.colCount}>{list.length} turno{list.length === 1 ? '' : 's'}</Text>
          ) : null}
        </TouchableOpacity>
        <ScrollView
          style={styles.colBody}
          contentContainerStyle={styles.colBodyContent}
          refreshControl={
            props.onRefresh ? (
              <RefreshControl
                refreshing={Boolean(props.refreshing)}
                onRefresh={props.onRefresh}
                colors={[agenda.brand]}
                tintColor={agenda.brand}
              />
            ) : undefined
          }
        >
          {list.length === 0 && dayOffs.length === 0 ? (
            props.onCreateAtDay ? (
              <TouchableOpacity
                style={styles.emptyCta}
                onPress={() => props.onCreateAtDay?.(day)}
                activeOpacity={0.85}
                accessibilityRole="button"
                accessibilityLabel={`Nuevo turno el ${day.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })}`}
              >
                <Text style={styles.emptyCtaText}>Libre — ＋ turno</Text>
              </TouchableOpacity>
            ) : (
              <Text style={styles.empty}>Libre</Text>
            )
          ) : (
            <>
              {list.map((a) => {
                const st = STATUS_STYLE[a.status] ?? STATUS_STYLE.confirmed;
                const statusLabel = STATUS_LABELS[a.status] ?? a.status;
                const svcName = props.productNameById.get(a.productId);
                return (
                <TouchableOpacity
                  key={a.id}
                  style={[styles.block, { borderColor: st.border, backgroundColor: st.bg }]}
                  onPress={() => props.onPressAppointment(a)}
                  activeOpacity={0.85}
                  accessibilityRole="button"
                  accessibilityLabel={
                    `${formatTime(a.startsAt)} — ${statusLabel} — ${a.customerName}` +
                    (svcName ? ` — ${svcName}` : '') +
                    (multiAgenda
                      ? ` — ${props.resourceNameById?.get(a.resourceId) ?? 'agenda'}`
                      : '')
                  }
                >
                  <View style={styles.blockTopRow}>
                    {multiAgenda ? (
                      <View
                        style={[
                          styles.blockDot,
                          { backgroundColor: props.resourceColorById?.get(a.resourceId) ?? agenda.ink2 },
                        ]}
                      />
                    ) : null}
                    <Text style={[styles.blockTime, { color: st.text }]} numberOfLines={1}>
                      {formatTime(a.startsAt)}
                      {/* Estado también en texto — no solo por color. */}
                      {a.status !== 'confirmed' ? ` · ${statusLabel}` : ''}
                    </Text>
                  </View>
                  <Text style={[styles.blockName, { color: st.text }]} numberOfLines={2}>
                    {a.customerName}
                  </Text>
                  {props.productNameById.get(a.productId) ? (
                    <Text style={[styles.blockSvc, { color: st.text }]} numberOfLines={1}>
                      {props.productNameById.get(a.productId)}
                    </Text>
                  ) : null}
                </TouchableOpacity>
                );
              })}
              {dayOffs.map((off) => (
                <View key={off.id} style={styles.offStrip}>
                  <Text style={styles.offStripText} numberOfLines={1}>
                    {props.resourceNameById?.get(off.resourceId) ?? 'Agenda'} —{' '}
                    {off.reason?.trim() || 'ausente todo el día'}
                  </Text>
                </View>
              ))}
            </>
          )}
        </ScrollView>
      </View>
    );
  });

  if (isWide) {
    return (
      <PaperSheet style={styles.paper}>
        <ScrollView
          style={[styles.root, styles.rowWide]}
          contentContainerStyle={styles.rowWideContent}
          refreshControl={
            props.onRefresh ? (
              <RefreshControl
                refreshing={Boolean(props.refreshing)}
                onRefresh={props.onRefresh}
                colors={[agenda.brand]}
                tintColor={agenda.brand}
              />
            ) : undefined
          }
        >
          {columns}
        </ScrollView>
      </PaperSheet>
    );
  }

  return (
    <PaperSheet style={styles.paper}>
      <ScrollView
        horizontal
        style={styles.root}
        contentContainerStyle={styles.row}
      >
        {columns}
      </ScrollView>
    </PaperSheet>
  );
}

const styles = StyleSheet.create({
  paper: { flex: 1, minHeight: 0 },
  root: { flex: 1, backgroundColor: agenda.surface },
  row: { flexGrow: 1 },
  rowWide: { alignSelf: 'stretch' },
  rowWideContent: { flexDirection: 'row', flexGrow: 1 },
  col: {
    width: 148,
    flexGrow: 1,
    minWidth: 132,
    borderRightWidth: 1,
    borderRightColor: agenda.border,
    backgroundColor: agenda.surface,
  },
  colWide: {
    width: undefined,
    flex: 1,
    minWidth: 0,
  },
  colAnchor: { backgroundColor: agenda.chooseBg },
  colHead: {
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: agenda.border,
    alignItems: 'center',
    backgroundColor: agenda.surface2,
  },
  /** Anchor = el día elegido; acento brand para que se vea de un vistazo. */
  colHeadAnchor: { borderBottomColor: agenda.brand, borderBottomWidth: 2 },
  colDow: {
    fontSize: 11,
    fontWeight: '700',
    color: agenda.ink2,
    textTransform: 'uppercase',
    fontFamily: agendaFonts.sans,
  },
  colNum: { fontSize: 18, fontWeight: '700', color: agenda.ink, fontFamily: agendaFonts.sans },
  colToday: { color: agenda.brand },
  colCount: { fontSize: 11, color: agenda.ink2, marginTop: 2, fontFamily: agendaFonts.sans },
  colBody: { flex: 1 },
  colBodyContent: { padding: 6, gap: 6 },
  empty: { fontSize: 12, color: agenda.ink2, textAlign: 'center', marginTop: 12, fontFamily: agendaFonts.sans },
  /** Proto .wempty — CTA punteado brand "Libre — ＋ turno". */
  emptyCta: {
    marginTop: 6,
    paddingVertical: 14,
    paddingHorizontal: 10,
    alignItems: 'center',
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: agenda.chooseBorder,
    borderRadius: agenda.radius,
    backgroundColor: agenda.chooseBg,
  },
  emptyCtaText: { fontSize: 12, fontWeight: '700', color: agenda.brand, fontFamily: agendaFonts.sans },
  /** Proto .woff — franja de ausencia al pie de la columna. */
  offStrip: {
    marginHorizontal: -6,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderTopWidth: 1,
    borderStyle: 'dashed',
    borderTopColor: agenda.border,
  },
  offStripText: { fontSize: 10, fontStyle: 'italic', color: agenda.ink3, fontFamily: agendaFonts.sans },
  block: {
    borderRadius: agenda.radiusBlock,
    borderWidth: 1,
    paddingHorizontal: 7,
    paddingVertical: 6,
  },
  blockTopRow: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  blockDot: { width: 6, height: 6, borderRadius: 3 },
  blockTime: { fontSize: 10, fontWeight: '700', fontFamily: agendaFonts.sans },
  blockName: { fontSize: 12, fontWeight: '700', marginTop: 2, fontFamily: agendaFonts.sans },
  blockSvc: { fontSize: 11, opacity: 0.8, marginTop: 1, fontFamily: agendaFonts.sans },
});
